import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Code,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Divider,
} from '@chakra-ui/react';

function ApiDocs() {
  const endpoints = [
    {
      category: 'Authentication',
      items: [
        {
          method: 'POST',
          path: '/api/v1/auth/login/',
          description: 'Login and get authentication token',
          body: { username: 'string', password: 'string' },
          response: { token: 'string' },
        },
      ],
    },
    {
      category: 'Customers',
      items: [
        {
          method: 'GET',
          path: '/api/customers/',
          description: 'List all customers',
          auth: true,
        },
        {
          method: 'POST',
          path: '/api/customers/',
          description: 'Create a new customer',
          auth: true,
          body: { email: 'string', name: 'string (optional)', stripe_id: 'string (optional)' },
        },
        {
          method: 'GET',
          path: '/api/customers/{id}/',
          description: 'Get customer details with subscriptions and payments',
          auth: true,
        },
        {
          method: 'PUT',
          path: '/api/customers/{id}/',
          description: 'Update a customer',
          auth: true,
          body: { email: 'string (optional)', name: 'string (optional)' },
        },
        {
          method: 'DELETE',
          path: '/api/customers/{id}/',
          description: 'Delete a customer',
          auth: true,
        },
      ],
    },
    {
      category: 'Subscriptions',
      items: [
        {
          method: 'GET',
          path: '/api/subscriptions/',
          description: 'List all subscriptions',
          auth: true,
        },
        {
          method: 'POST',
          path: '/api/subscriptions/',
          description: 'Create a new subscription',
          auth: true,
          body: { customer_id: 'integer', product: 'string', status: 'string (optional)' },
        },
        {
          method: 'GET',
          path: '/api/subscriptions/{id}/',
          description: 'Get subscription details with events',
          auth: true,
        },
        {
          method: 'PUT',
          path: '/api/subscriptions/{id}/',
          description: 'Update a subscription',
          auth: true,
          body: { product: 'string (optional)', status: 'string (optional)' },
        },
        {
          method: 'DELETE',
          path: '/api/subscriptions/{id}/',
          description: 'Delete a subscription',
          auth: true,
        },
      ],
    },
    {
      category: 'Payments',
      items: [
        {
          method: 'GET',
          path: '/api/payments/',
          description: 'List all payments',
          auth: true,
        },
        {
          method: 'POST',
          path: '/api/payments/',
          description: 'Create a new payment record',
          auth: true,
          body: { customer_id: 'integer', amount: 'integer (cents)', currency: 'string', status: 'string', description: 'string (optional)' },
        },
        {
          method: 'GET',
          path: '/api/payments/{id}/',
          description: 'Get payment details with refunds',
          auth: true,
        },
        {
          method: 'PUT',
          path: '/api/payments/{id}/',
          description: 'Update a payment',
          auth: true,
          body: { status: 'string (optional)', description: 'string (optional)' },
        },
        {
          method: 'DELETE',
          path: '/api/payments/{id}/',
          description: 'Delete a payment',
          auth: true,
        },
      ],
    },
    {
      category: 'Refunds',
      items: [
        {
          method: 'GET',
          path: '/api/refunds/',
          description: 'List all refunds',
          auth: true,
        },
        {
          method: 'POST',
          path: '/api/refunds/',
          description: 'Create a new refund',
          auth: true,
          body: { payment_id: 'integer', amount: 'integer (cents)', status: 'string', reason: 'string (optional)' },
        },
        {
          method: 'PUT',
          path: '/api/refunds/{id}/',
          description: 'Update a refund',
          auth: true,
          body: { status: 'string (optional)', reason: 'string (optional)' },
        },
        {
          method: 'DELETE',
          path: '/api/refunds/{id}/',
          description: 'Delete a refund',
          auth: true,
        },
      ],
    },
    {
      category: 'API Keys',
      items: [
        {
          method: 'GET',
          path: '/api/api-keys/',
          description: 'List all API keys',
          auth: true,
        },
        {
          method: 'POST',
          path: '/api/api-keys/',
          description: 'Create a new API key',
          auth: true,
          body: { name: 'string', description: 'string (optional)', is_active: 'boolean (optional)' },
        },
        {
          method: 'PUT',
          path: '/api/api-keys/{id}/',
          description: 'Update an API key',
          auth: true,
          body: { name: 'string (optional)', description: 'string (optional)', is_active: 'boolean (optional)' },
        },
        {
          method: 'DELETE',
          path: '/api/api-keys/{id}/',
          description: 'Delete an API key',
          auth: true,
        },
      ],
    },
    {
      category: 'Public Payment API',
      items: [
        {
          method: 'POST',
          path: '/api/v1/payments/create-subscription',
          description: 'Create a new Stripe subscription',
          headers: { 'X-API-KEY': 'Your API key' },
          body: { paymentMethodId: 'string', productId: 'string', email: 'string' },
        },
        {
          method: 'POST',
          path: '/api/v1/payments/create-one-off-payment',
          description: 'Create a one-off payment',
          headers: { 'X-API-KEY': 'Your API key' },
          body: { paymentMethodId: 'string', price: 'string (amount)', email: 'string' },
        },
        {
          method: 'POST',
          path: '/api/v1/webhook',
          description: 'Stripe webhook endpoint',
        },
      ],
    },
    {
      category: 'PostgreSQL Status',
      items: [
        {
          method: 'GET',
          path: '/api/postgres/status/',
          description: 'Get server version, uptime, database size, connection stats and configuration',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'GET',
          path: '/api/postgres/tables/',
          description: 'List tables with sizes, row counts, and vacuum stats (params: page, sort, search)',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'GET',
          path: '/api/postgres/activity/',
          description: 'Get active queries and connections from pg_stat_activity',
          headers: 'Authorization: Token your-token',
        },
        {
          method: 'POST',
          path: '/api/postgres/query/',
          description: 'Execute a read-only SQL query (body: {"sql": "SELECT ..."}). Only SELECT statements allowed.',
          headers: 'Authorization: Token your-token',
          body: { sql: 'SELECT * FROM payments_customer LIMIT 10' },
          response: '{ "success": true, "columns": [...], "rows": [...], "row_count": N }',
        },
      ],
    },
    {
      category: 'Insights',
      items: [
        {
          method: 'GET',
          path: '/api/tenants/',
          description: 'Tenant overview — lists all distinct tenants with resource counts, first seen/last active timestamps, and share percentages',
          headers: 'Authorization: Token your-token',
          response: '{ "tenants": [...], "total_tenants": N, "total_records": N }',
        },
        {
          method: 'GET',
          path: '/api/analytics/',
          description: 'Usage analytics — time-series data for admin activity, customers, payments, webhooks. Supports ?days= param (default 30, max 365). Tenant-scoped via X-Tenant-ID header.',
          headers: 'Authorization: Token your-token',
          response: '{ "period_days": 30, "audit_by_day": [...], "customers_by_day": [...], ... }',
        },
        {
          method: 'GET',
          path: '/api/health/',
          description: 'Health check — verifies database connectivity, Stripe API reachability, and configuration completeness. Returns overall status (healthy/degraded/unhealthy/not_configured).',
          headers: 'Authorization: Token your-token',
          response: '{ "overall": "healthy", "checks": { "database": {...}, "stripe_api": {...}, "configuration": {...} } }',
        },
      ],
    },
  ];

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'green';
      case 'POST': return 'blue';
      case 'PUT': return 'orange';
      case 'DELETE': return 'red';
      default: return 'gray';
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>API Documentation</Heading>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
        <Heading size="md" mb={4}>Authentication</Heading>
        <Text mb={4}>
          All admin API endpoints require authentication using a token. Include the token in the 
          Authorization header:
        </Text>
        <Code p={3} display="block" borderRadius="md" bg="gray.50">
          Authorization: Token your-token-here
        </Code>
        <Text mt={4}>
          Public payment API endpoints use the <Code>X-API-KEY</Code> header instead.
        </Text>
      </Box>

      <Accordion allowMultiple>
        {endpoints.map((category, idx) => (
          <AccordionItem key={idx} bg="white" mb={2} borderRadius="lg" boxShadow="sm">
            <AccordionButton py={4}>
              <Box flex="1" textAlign="left">
                <Heading size="sm">{category.category}</Heading>
              </Box>
              <Badge colorScheme="teal" mr={2}>{category.items.length} endpoints</Badge>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <VStack align="stretch" spacing={4}>
                {category.items.map((endpoint, endpointIdx) => (
                  <Box key={endpointIdx} p={4} bg="gray.50" borderRadius="md">
                    <HStack mb={2}>
                      <Badge colorScheme={getMethodColor(endpoint.method)}>
                        {endpoint.method}
                      </Badge>
                      <Code fontSize="sm">{endpoint.path}</Code>
                      {endpoint.auth && (
                        <Badge colorScheme="yellow" variant="subtle">Auth Required</Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      {endpoint.description}
                    </Text>
                    {endpoint.body && (
                      <>
                        <Text fontSize="xs" fontWeight="bold" mb={1}>Request Body:</Text>
                        <Code fontSize="xs" display="block" p={2} borderRadius="md">
                          {JSON.stringify(endpoint.body, null, 2)}
                        </Code>
                      </>
                    )}
                    {endpoint.headers && (
                      <>
                        <Text fontSize="xs" fontWeight="bold" mb={1} mt={2}>Headers:</Text>
                        <Code fontSize="xs" display="block" p={2} borderRadius="md">
                          {JSON.stringify(endpoint.headers, null, 2)}
                        </Code>
                      </>
                    )}
                    {endpoint.response && (
                      <>
                        <Text fontSize="xs" fontWeight="bold" mb={1} mt={2}>Response:</Text>
                        <Code fontSize="xs" display="block" p={2} borderRadius="md">
                          {JSON.stringify(endpoint.response, null, 2)}
                        </Code>
                      </>
                    )}
                  </Box>
                ))}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
}

export default ApiDocs;
